import styled, { css } from 'styled-components';
import { ACTIVE_BOX_SHADOW } from 'components/ButtonBase';
import IconButton from 'components/IconButton';
import { getThemeColor } from 'utils/styled';
import { ICON_CLASSNAME } from './constants';
import { StyledRootProps } from './types';

const DEFAULT_BOX_SHADOW = '0 4px 10px 0 rgba(0, 0, 0, 0.1)';
const HOVER_BOX_SHADOW = '0 4px 30px 0 rgba(0, 0, 0, 0.1)';

const frostedVariantStyle = css`
  &,
  &[data-hovered='true'] {
    background-color: rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(30px);
  }
`;

const solidVariantStyle = css<StyledRootProps>`
  &,
  &[data-hovered='true'],
  &[data-hovered='true']:not([disabled]) {
    background-color: ${(props) =>
      props.$color || getThemeColor('actionPrimary')};
  }
`;

export const Root = styled(IconButton)<StyledRootProps>`
  border-radius: 50%;
  height: 88px;
  width: 88px;

  // remove the default active box shadow.  the ActionButton sometimes has a blur
  // filter, which then blurs the box shadow.  the active shadow gets reapplied
  // in on the Inner component where it won't get blurred
  &:active:not([disabled]) {
    box-shadow: none;
  }

  &,
  &[data-hovered='true'],
  &[data-hovered='true']:not([disabled]) {
    background-color: transparent;
    color: ${getThemeColor('l2')};
  }

  &[data-hovered='true'] {
    transform: scale(1.045);
  }

  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'solid':
        return `
          box-shadow: ${DEFAULT_BOX_SHADOW};
          transition: ${theme.transitions.create(
            ['box-shadow', 'transform'],
            '300ms',
          )};

          &,
          &[data-hovered='false'] {
            box-shadow: ${DEFAULT_BOX_SHADOW};
          }

          &[data-hovered='true'],
          &[data-hovered='true']:active:not([disabled]) {
            box-shadow: ${HOVER_BOX_SHADOW};
          }
        `;

      case 'frosted':
        return `
          transition: ${theme.transitions.create('transform', '300ms')};
        `;

      default:
        return undefined;
    }
  }}
`;

export const Inner = styled.div<StyledRootProps>`
  align-items: center;
  border-radius: inherit;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  ${Root}:active:not([disabled]) & {
    box-shadow: ${ACTIVE_BOX_SHADOW};
  }

  ${Root}[disabled] &:active {
    box-shadow: none;
  }

  & > .${ICON_CLASSNAME} {
    height: 3em;
    width: 3em;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'solid':
        return solidVariantStyle;

      case 'frosted':
        return frostedVariantStyle;

      default:
        return undefined;
    }
  }}
`;

export const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const Label = styled.label`
  ${({ theme }) => theme.typography.variants.fabLabel.default}
  color: ${getThemeColor('d4')};
  margin-top: ${({ theme }) => theme.spacing.create(2)};
  user-select: none;
`;
